import React, { useState, useEffect } from 'react';

import { Images } from 'util/images';
import * as appConstants from 'util/appConstants';
import {
  TrophyIcon,
  SortArrows,
  TableTitle,
  ChallengeTableHeader,
  NameLink,
  TotalSubmits,
  ViewGameContainer,
  TrendIcon,
  LanguageIcon,
} from './styles';
import { SearchSelect } from 'components/searchSelect/searchSelect';
import RankListPlayBack from './rankListPlayBack';
import { distributeTrophies } from 'functions/distributeTrophies';
import { Spinner } from 'components/Spinner/spinner';

export function RankList({
  isTournament,
  rankList,
  gamePlay,
  tournamentDetails,
  pixiLoadingStatus,
  isOfficialChallenge,
  isHackathon,
  onSelectedRow,
}) {
  const languages = appConstants.contestLanguages;

  const [filteredRankList, setFilteredRankList] = useState(rankList);
  const [isAscending, setIsAscending] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(appConstants.contestLanguages[0]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [nameQuery, setNameQuery] = useState('');

  useEffect(() => {
    setFilteredRankList(isOfficialChallenge ? distributeTrophies(rankList) : rankList);
  }, [rankList, isOfficialChallenge]);

  const sortRankList = () => {
    let sortedData = filteredRankList.slice().sort((a, b) => {
      return isAscending ? b.maxScore - a.maxScore : a.maxScore - b.maxScore;
    });
    setFilteredRankList(sortedData);
    setIsAscending(!isAscending);
    setSelectedRowIndex(null);
  };

  const sortRatings = () => {
    let sortedData = filteredRankList.slice().sort((a, b) => {
      if (a?.eligibleStatus?.eligible === false) return 1;
      if (b?.eligibleStatus?.eligible === false) return -1;
      if (a.rating === null) return isAscending ? 1 : -1;
      if (b.rating === null) return isAscending ? -1 : 1;
      return isAscending ? b.rating - a.rating : a.rating - b.rating;
    });
    setFilteredRankList(sortedData);
    setIsAscending(!isAscending);
    setSelectedRowIndex(null);
  };

  const searchByLanguage = (language) => {
    if (language) {
      if (!language.value) {
        setSelectedLanguage(appConstants.contestLanguages[0]);
        setFilteredRankList(rankList);
      } else {
        let updatedList = rankList.filter((participants) => {
          if (language.value.toLowerCase() !== appConstants.contestLanguages[1].value.toLowerCase()) {
            if (participants.language.toLowerCase() === language.value.toLowerCase()) {
              return participants;
            } else return false;
          } else if (participants.language.toLowerCase() === language.value.toLowerCase()) {
            return participants;
          } else return false;
        });
        setSelectedLanguage(language);
        setFilteredRankList(updatedList);
      }
      setSelectedRowIndex(null);
    }
  };

  const getDeviconIconClassName = (language) => {
    let iconLanguage = language;
    switch (language) {
      case 'go':
        return `devicon-go-original-wordmark colored mr-1`;
      case 'c_sharp':
        iconLanguage = 'csharp';
        break;
      case 'cpp':
        iconLanguage = 'cplusplus';
        break;
    }
    return `devicon-${iconLanguage}-plain colored mr-1`;
  };

  const renderTableBody = () => {
    let count = 0;
    return filteredRankList.map((item) => {
      if (item.name.toLowerCase().search(nameQuery) === -1) return false;
      count++;
      return (
        <div key={count}>
          <ChallengeTableHeader isGrey={count % 2 === 0} className="row py-3">
            <TableTitle className="col-1">{count}</TableTitle>
            <TableTitle className="col-5">
              <TrophyIcon
                itemColor={item.color}
                data-toggle="tooltip"
                title="Trophy will be awarded on 25th of every month"
              />
              {item.accountId ? <NameLink href={`/profile?id=${item.accountId}`}>{item.name}</NameLink> : item.name}
            </TableTitle>
            <TableTitle className="col-4 col-sm-2 col-md-2 col-lg-2 col-xl-2">
              <LanguageIcon className={getDeviconIconClassName(item.language)} />
              {item.languageVersion}
            </TableTitle>
            <TableTitle className="col-2">{item.maxScore}</TableTitle>
            <TableTitle className="col-2">
              <TotalSubmits className="d-none d-md-block d-lg-block d-xl-block">{item.numSubmits}</TotalSubmits>
            </TableTitle>
          </ChallengeTableHeader>
        </div>
      );
    });
  };

  const selectedRow = (index, item) => {
    if (selectedRowIndex === index) {
      setSelectedRowIndex(null);
    } else {
      setSelectedRowIndex(index);
      onSelectedRow(item);
    }
  };

  const renderChallengeTable = () => {
    return (
      <div className="col-12 px-1">
        <div className="row py-2">
          <TableTitle isHash="true" className="col-1">
            #
          </TableTitle>
          <TableTitle className="col-5">Name</TableTitle>
          <TableTitle className="col-4 col-sm-2 col-md-2 col-lg-2 col-xl-2">
            <SearchSelect
              clearable={false}
              options={languages}
              onChange={searchByLanguage}
              value={selectedLanguage}
              placeholder="Language"
              className="VirtualizedSelectCustom"
            />
          </TableTitle>
          <TableTitle isScore="true" onClick={() => sortRankList()} className="col-2">
            {isTournament ? 'Rating' : 'Score'}{' '}
            <SortArrows alt="arrow" src={isAscending ? Images.downArrow : Images.upArrow} />
          </TableTitle>
          <TableTitle className="col-2 d-none d-md-flex d-lg-flex d-xl-flex">Submits</TableTitle>
        </div>
        {renderTableBody()}
      </div>
    );
  };

  const renderGame = () => {
    if (!pixiLoadingStatus) {
      return <RankListPlayBack matchData={gamePlay} testImplementation={tournamentDetails} />;
    } else {
      return (
        <div style={{ padding: 10 }}>
          <Spinner />
        </div>
      );
    }
  };

  const renderTournamentTopList = () => {
    let count = 0;
    return filteredRankList.map((item, index) => {
      if (item.name.toLowerCase().search(nameQuery) === -1) return false;
      count++;
      return (
        <div className={isHackathon ? 'row' : ''} key={count}>
          <ChallengeTableHeader
            isGrey={count % 2 === 0}
            ineligible={item?.eligibleStatus?.eligible === false}
            className="row py-3"
          >
            <TableTitle className="col-1">
              {count}
              {item.trophyType && <img className="awardedTrophy" src={item.trophyType} alt="" />}
            </TableTitle>
            <TableTitle
              className={isHackathon ? 'col-3 col-md-5 col-lg-5 col-xl-5' : 'col-5 col-md-4 col-lg-4 col-xl-4'}
            >
              <TrophyIcon
                itemColor={item.color}
                data-toggle="tooltip"
                title="Trophy will be awarded on 25th of every month"
              />
              {'trend' in item && (
                <TrendIcon
                  className={
                    item.trend === 1
                      ? 'fa fa-arrow-circle-up'
                      : item.trend === -1
                      ? 'fa fa-arrow-circle-down'
                      : 'fa fa-arrow-circle-right'
                  }
                  isHackathon={isHackathon}
                  isUp={item.trend === 1}
                  isDown={item.trend === -1}
                  isConstant={item.trend === 0}
                  data-toggle="tooltip"
                  title="Position as compare to last round"
                />
              )}
              {item.accountId ? <NameLink href={`/profile?id=${item.accountId}`}>{item.name}</NameLink> : item.name}
            </TableTitle>
            <TableTitle className="col-4 col-md-2 col-lg-2 col-xl-2">
              <LanguageIcon className={getDeviconIconClassName(item.language)} isHackathon={isHackathon} />
              {item.languageVersion}
            </TableTitle>
            <TableTitle isScore="true" className={isHackathon ? 'col-3' : 'col-2'}>
              {item?.eligibleStatus?.eligible === true
                ? item.rating === null
                  ? 'Not ranked'
                  : Math.round(item.rating)
                : item?.eligibleStatus?.reason}
            </TableTitle>

            <TableTitle className="col-1 d-none d-md-block d-lg-block d-xl-block">
              <TotalSubmits>{item.numSubmits}</TotalSubmits>
            </TableTitle>
            {!isHackathon && (
              <TableTitle className="col-2 d-none d-md-block d-lg-block d-xl-block">
                <button className="btn challengeButton  " onClick={() => selectedRow(index, item)}>
                  {selectedRowIndex === index ? 'Hide Match' : 'View Match'}
                </button>
              </TableTitle>
            )}
          </ChallengeTableHeader>
          <ViewGameContainer id="viewGame">{selectedRowIndex === index ? renderGame() : null}</ViewGameContainer>
        </div>
      );
    });
  };

  const renderTournamentTable = () => {
    return (
      <div className="12 px-1 ">
        <div className="row py-2">
          <TableTitle isHash={isHackathon ? false : true} className="col-1">
            #
          </TableTitle>
          <TableTitle className={isHackathon ? 'col-3 col-md-5 col-lg-5 col-xl-5' : 'col-5 col-md-4 col-lg-4 col-xl-4'}>
            Name
          </TableTitle>
          <TableTitle className="col-4 col-md-2 col-lg-2 col-xl-2">
            <SearchSelect
              clearable={false}
              options={languages}
              onChange={searchByLanguage}
              value={selectedLanguage}
              placeholder="Language"
              className="w-100"
            />
          </TableTitle>
          <TableTitle isScore="true" onClick={() => sortRatings()} className={isHackathon ? 'col-3' : 'col-2'}>
            Rating <SortArrows alt="arrow" src={isAscending ? Images.downArrow : Images.upArrow} />
          </TableTitle>
          <TableTitle className="col-1 d-none d-md-flex d-lg-flex d-xl-flex">Submits</TableTitle>
          {!isHackathon && <TableTitle className="col-2 d-none d-md-flex d-lg-flex d-xl-flex" />}
        </div>
        {renderTournamentTopList()}
      </div>
    );
  };

  return (
    <div>
      {!isHackathon && (
        <input
          type="text"
          className="form-control mb-1"
          onChange={(event) => setNameQuery(event.target.value.toLowerCase())}
          placeholder="Search by name..."
        />
      )}
      {isTournament ? renderTournamentTable() : renderChallengeTable()}
    </div>
  );
}
